import {useReducer, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'react-scrollbars-custom';
import info from '../../images/newDesign/infoFilled.svg';
import { showLoader, hideLoader } from '../../store/common/slice';
import Button from '../../common/Button';
import InputGroup from '../../common/InputGroup';
import Input from '../../common/simple/input';
import DateMonthPicker from '../../common/DatepickerPortal';
import { SquareCheckbox } from '../../ui/SquareCheckbox';
import download from '../../images/newDesign/download.svg';
import UploadFiles from './UploadFiles';
import {useFormik} from "formik";
import {NotificationManager} from "react-notifications";
import * as XLSX from 'xlsx';

// variables for excel file column names
const aptHe = 'דירה';
const amountHe = 'סכום לעדכון';

function UpdateFlexibleRates({ apartments, onSubmit }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const [manualUpdate, setManualUpdate] = useState(true);
    const [uploadItem, setUploadItem] = useState(null);
    const currencyFromServer = useSelector(state => state.buildings.currentBuildingData?.currency ?? state.buildings.managerData?.currency);
    const currency = currencyFromServer ? t(`currency.${currencyFromServer}`) : t('currency.ils');

    const initialValues = {
        addToExistingRate: false,
        amounts: {},
    }
    const form = useFormik({
        initialValues: initialValues,
        onSubmit: handleFormSubmit,
    });

    function handleFormSubmit(values, helpers) {
        // validate if date range exists
        if (!values.startDate || !values.endDate) {
            NotificationManager.warning(t('payment_settings.provideDate'));
            return
        }

        const amountKeys = Object.keys(values.amounts);
        if (amountKeys.length === 0) {
            NotificationManager.warning(t('payment_settings.noAmountToUpdate'));
        } else {
            onSubmit(values);
            helpers.resetForm(initialValues)
        }
    }

    // parsing excel file
    function readFile(file) {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        return new Promise((resolve, reject) => {
            reader.onload = async (e) => {
            const cfb = XLSX.read(e.target.result, {type: 'binary'});
            const sheetData = cfb.Sheets[cfb.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(sheetData);
            resolve(jsonData)
        }})
    }

    async function handleUploadClick(e) {
        e.preventDefault();
        // setting progress to 100% since it's not sent on BE
        uploadItem.setUploadProgress(uploadItem.size);
        
        dispatch(showLoader());
        
        let values = {};
        // parsing excel and extracting amounts
        const data = await readFile(uploadItem.file);
        
        for (const i of data) {
            values[i[aptHe]] = i[amountHe]
        }
        // prefilling form in manual update tab with values from excel
        form.setFieldValue('amounts', Object.assign({}, values))       

        NotificationManager.success(t('reports.fileUploaded'));
        // giving users time to read the notification and realize they will be redirected
        setTimeout(() => {
            dispatch(hideLoader());
    
            // automatically changing tab
            setManualUpdate(true);
            // clearing uploaded file
            setUploadItem(null);
        }, 5000);
    }

    function ApartmentCard({ apartment, value, onChange }) {
        const apartmentNumber = apartment.ApartmentNumber;
        const [amount, setAmount] = useState(value);
        function handleInputChange(e) {
            const value = e.target.value;
            const v = value === '' ? undefined : Number(value);
            setAmount(v);
        }
        function handleInputBlur() {
            onChange(amount);
        }
        return (
            <div className='apt-card'>
                <div className='apt-card-text'>{apartmentNumber}</div>
                <div className='apt-card-text'>{apartment.name}</div>
                <Input type="number" placeholder={`- ${currency}`} value={amount} onChange={handleInputChange} onBlur={handleInputBlur} />
            </div>
        )
    }

    return (
        <div className='rates flexible'>
            <form onSubmit={form.handleSubmit}>

                <div className='update-info'>
                    <img src={info} />
                    <div>{t('payment_settings.updateFlexibleAmount')}</div>
                </div>

                <Scrollbar
                    style={{ width: '100%', height: '350px' }}
                    trackYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-track" />;
                        }
                    }}
                    thumbYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                        }
                    }}
                    rtl={false}
                    noScrollX
                >
                    <div className='switch-manual-update'>
                        <div 
                            className={`switch-btn ${!manualUpdate ? 'selected' : ''}`}
                            onClick={() => setManualUpdate(false)}
                        >
                            {t('payment_settings.excelUpdate')}
                        </div>
                        <div 
                            className={`switch-btn ${manualUpdate ? 'selected' : ''}`}
                            onClick={() => setManualUpdate(true)}
                        >
                            {t('payment_settings.manualUpdate')}
                        </div>
                    </div> 

                    {manualUpdate ? (
                        <div className='manual-update'>
                            <div className='title-row'>
                                <div>{t('reports.apartmentName')}</div>
                                <div>{t('reports.tenantName')}</div>
                                <div className='update-amount'>{t('payment_settings.amountUpdate')}</div>
                            </div>
                            <div className='apt-cards-wrapper'>
                                {apartments.map(a => (
                                    <ApartmentCard
                                        key={a.ApartmentNumber}
                                        apartment={a}
                                        value={form.values.amounts[a.ApartmentNumber]}
                                        onChange={v => form.setFieldValue('amounts', Object.assign({}, form.values.amounts, { [a.ApartmentNumber]: v }))}
                                    />
                                ))}
                            </div>
                        </div>
                    ): (
                        <>
                            <div className='excel-example'>
                                <div>{t('payment_settings.exampleExcel')}</div>
                                <a href="https://bllink-public.s3.us-east-1.amazonaws.com/update_apartment_rates.xlsx" onClick={() => false}>
                                    <img src={download} />
                                </a>
                            </div>
                            <UploadFiles uploadItem={uploadItem} setUploadItem={setUploadItem} />              
                        </>
                    )}
                </Scrollbar>

                <div className='datepickers-and-checkbox-wrapper'>
                    {manualUpdate ? (
                        <>
                            <div className='datepickers-container'>
                                <InputGroup
                                    labelText={`${t('payment_settings.startingFrom')}`}
                                    labelFor='from'
                                    labelClassName='datepicker-label'
                                >
                                    <DateMonthPicker
                                        date={form.values.startDate}
                                        onChange={d => form.setFieldValue('startDate', d)}
                                        id='from'
                                        maxDate={form.values.endDate}
                                        language={i18n.language}
                                        className='one-line-design classic-bllink'
                                        withPortal={false}
                                        ltr={ltr}
                                        showMonthYear
                                    />
                                </InputGroup>

                                <InputGroup
                                    labelText={`${t('payment_settings.upTo')}`}
                                    labelFor='to'
                                    labelClassName='datepicker-label'
                                >
                                    <DateMonthPicker
                                        date={form.values.endDate}
                                        onChange={d => form.setFieldValue('endDate', d)}
                                        id='to'
                                        minDate={form.values.startDate}
                                        language={i18n.language}
                                        className='one-line-design classic-bllink'
                                        withPortal={false}
                                        ltr={ltr}
                                        showMonthYear
                                    />
                                </InputGroup>
                            </div>

                            <div className='checkbox-and-btn'>
                                <SquareCheckbox
                                    checked={form.values.addToExistingRate}
                                    onChange={checked => form.setFieldValue('addToExistingRate', checked)}
                                    label={t('payment_settings.addToExistingRate')}
                                    containerClassName='checkbox'
                                    className='checkbox-input'
                                    labelFontWeight='400'
                                />

                                <Button type="submit">{t('payment_settings.update')}</Button>
                            </div>                   
                        </>
                    ) : (
                        <div className='checkbox-and-btn'>
                            <div></div>
                            <Button onClick={handleUploadClick}>{t('payment_settings.uploadFile')}</Button>
                        </div>
                    )}
                </div>

            </form>
        </div>
    )
}

export default UpdateFlexibleRates;
